import React, { useState, useEffect } from 'react';
import { Checkbox, DropDown, TextArea } from '../../../shared/inputs/base';
import Grid, { gridSortType, orderGridDataFn } from '../../../shared/Grid/Grid';
import FormFieldLabel from '../../../shared/Form/FormFieldLabel';
import { ColumnType } from '../../../shared/Grid/types/Column';
import styled from 'styled-components';

import Button from '../../../shared/SmallComponents/Button';
import Input from '../../../shared/SmallComponents/Input';
import Modal from '../../../shared/Modal/Modal';
import { userDetails } from '../../../../state/ducks/users/users';
import { UserDropDownForm } from '../../../shared/inputs/form';
import { Formik, Form, Field, FieldProps } from 'formik';
import CZDatePicker from '../../../shared/SmallComponents/DatePicker';
import { FollowUp, shipment } from '../../../../models/entities/shipment/shipment';
import data from '../../../../sideEffects/company/settings/mockData';
import { DeleteFilled, EditOutlined } from '@ant-design/icons';
import { userSettings } from '../../../../models/entities/user';
import { filterColsByUserSettings } from '../../../shared/Grid/util';

interface IFollowUpExtended extends FollowUp {
    state: string;
    previousState: string;
}
type Props = {
    shipment: shipment;
    addNewFollowUp: (shipmentId: string, followUp: FollowUp) => Promise<void>;
    updateFollowUpCompletionStatus: (shipmentId: string, followUpId: number, isCompleted: boolean) => Promise<void>;
    deleteFollowUp: (shipmentId: string, followUpId: number) => Promise<void>;
    users: userDetails[];
    currentUserId: string;
    updateFollowUp: (shipmentId: string, followUp: FollowUp) => Promise<void>;
    followupGridSettings?: userSettings;
};

const FollowUpTab = ({
    followupGridSettings,
    updateFollowUp,
    shipment,
    addNewFollowUp,
    updateFollowUpCompletionStatus,
    deleteFollowUp,
    users,
    currentUserId
}: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [followUps, setFollowUps] = useState<Array<IFollowUpExtended>>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedTask, setSelectedTask] = useState<IFollowUpExtended | null>(null);
    const [gridSort, setGridSort] = useState<gridSortType<IFollowUpExtended>>({
        dueDate: 'asc'
    });

    const followupGridColumns: ColumnType<IFollowUpExtended>[] = [
        {
            title: 'To Do',
            field: 'todo',
            sortable: true,
            isDisplayed: true,
            width: '250px'
        },
        {
            title: 'Current State',
            field: 'state',
            sortable: true,
            isDisplayed: true,
            width: '150px'
        },
        {
            title: 'Previous State',
            field: 'previousState',
            sortable: true,
            isDisplayed: true,
            width: '150px'
        },
        {
            title: 'Due Date',
            field: 'dueDate',
            sortable: true,
            isDisplayed: true,
            width: '150px',
            columnType: 'date'
        },
        {
            title: 'Assigned To',
            field: 'assignedTo',
            sortable: true,
            isDisplayed: true,
            width: '200px'
        },
        {
            title: 'Created By',
            field: 'createdBy',
            sortable: true,
            isDisplayed: true,
            width: '150px',
            columnType: 'createdBy'
        },
        {
            title: 'Updated By',
            field: 'updatedBy',
            sortable: true,
            isDisplayed: true,
            width: '150px',
            columnType: 'updatedBy'
        },
        {
            title: 'Completed',
            field: 'isCompleted',
            sortable: true,
            isDisplayed: true,
            width: '120px',
            // columnType: 'bool',
            bindedFields: ['id', 'isCompleted'],
            renderer(cellValue, bindedData) {
                return (
                    <Checkbox
                        checked={(bindedData as any)[1] as boolean}
                        onChange={(e) => updateFollowUpCompletionStatus(shipment.id, (bindedData as any)[0], e.target.checked)}
                    />
                );
            }
        },
        {
            title: 'Actions',
            field: 'id',
            sortable: false,
            isDisplayed: true,
            width: '200px',
            bindedFields: ['id', 'shipmentId', 'todo', 'dueDate', 'assignedTo', 'isCompleted'],
            renderer: (cellValue, bindedData) => {
                const handleEdit = () => {
                    const followUp = followUps.find((f) => f.id === bindedData?.[0]);
                    if (followUp) {
                        setSelectedTask(followUp);
                        setIsEditMode(true);
                        setIsModalOpen(true);
                    }
                };

                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button onClick={handleEdit}>
                            <EditOutlined />
                        </Button>
                        <Button
                            onClick={() => {
                                if (window.confirm('Are you sure you want to delete this follow-up task?')) {
                                    deleteFollowUp(bindedData?.[1], bindedData?.[0]);
                                }
                            }}
                        >
                            <DeleteFilled />
                        </Button>
                    </div>
                );
            }
        }
    ];

    const setInitialValues = () => {
        if (isEditMode && selectedTask) {
            return {
                todo: selectedTask.todo,
                dueDate: new Date(selectedTask.dueDate),
                assignedTo: selectedTask.assignedTo.includes(' ')
                    ? users.find((u) => `${u.firstName} ${u.lastName}` === selectedTask.assignedTo)?.id || currentUserId
                    : selectedTask.assignedTo,
                isCompleted: selectedTask.isCompleted
            };
        }
        return {
            todo: '',
            dueDate: new Date(),
            assignedTo: currentUserId,
            isCompleted: false
        };
    };

    const validate = (values: any) => {
        const errors: any = {};
        if (!values.todo) {
            errors.todo = 'Task is required';
        }
        if (!values.dueDate) {
            errors.dueDate = 'Due Date is required';
        }
        if (!values.assignedTo) {
            errors.assignedTo = 'Assignee is required';
        }
        return errors;
    };
    const submit = async (values: any) => {
        if (isEditMode && selectedTask) {
            // For editing an existing follow-up - use updateFollowUp instead of addNewFollowUp
            await updateFollowUp(shipment.id, {
                ...values,
                id: selectedTask.id
            });
        } else {
            // For adding a new follow-up
            await addNewFollowUp(shipment.id, values);
        }
        setIsModalOpen(false);
        setIsEditMode(false);
        setSelectedTask(null);
    };
    useEffect(() => {
        const userMap: Map<string, userDetails> = new Map();

        users.forEach((user) => {
            userMap.set(user.id, user);
        });
        const eventsMap: Map<string, string> = new Map();
        data.en.state.forEach((state) => {
            eventsMap.set(state.code, state.name);
        });
        
        let processedFollowUps = shipment.followUps
            ? shipment.followUps.map((followUp) => {
                  const user = userMap.get(followUp.assignedTo);
                  const assignedTo = user ? `${user.firstName} ${user.lastName}` : 'N/A';
                  const followUpExtended: IFollowUpExtended = {
                      ...followUp,
                      state: eventsMap.get(shipment.state) || 'N/A',
                      previousState: eventsMap.get(shipment.previousState) || 'N/A',
                      assignedTo
                  };
                  return followUpExtended;
              })
            : [];

        // Sort follow-ups: active tasks first, then completed tasks (both groups respect user sorting)
        const activeFollowUps = processedFollowUps.filter(followUp => !followUp.isCompleted);
        const completedFollowUps = processedFollowUps.filter(followUp => followUp.isCompleted);
        
        // Apply sorting to each group separately
        const sortToApply = Object.keys(gridSort).length > 0 ? gridSort : { dueDate: 'asc' as const };
        const sortedActiveFollowUps = orderGridDataFn(activeFollowUps, sortToApply, followupGridColumns);
        const sortedCompletedFollowUps = orderGridDataFn(completedFollowUps, sortToApply, followupGridColumns);
        
        // Combine with active tasks first, completed tasks at the end
        const sortedFollowUps = [...sortedActiveFollowUps, ...sortedCompletedFollowUps];
        
        setFollowUps(sortedFollowUps);
    }, [shipment.followUps, shipment.events, shipment.state, gridSort, users]);
    return (
        <PageWrapper>
            <HeaderContainer>
                <h2>Follow-up Tasks</h2>
                <Button
                    onClick={() => {
                        setIsEditMode(false);
                        setSelectedTask(null);
                        setIsModalOpen(true);
                    }}
                >
                    Add New Follow-up
                </Button>
            </HeaderContainer>
            <GridWrapper>
                {shipment.followUps?.length ? (
                    <Grid<IFollowUpExtended>
                        columns={filterColsByUserSettings(followupGridColumns, followupGridSettings)}
                        data={followUps}
                        idKey="id"
                        emptyStateText="No follow-up tasks found for this shipment"
                        settings={followupGridSettings}
                        userSettingKey="followupGridSettingsTab"
                        onSettingsClick={() => console.log('settings')}
                        onRowMoreOptionsClick={(row) => console.log(row)}
                        colorRowsBy={(row) => row.isCompleted ? 'rgba(0, 0, 0, 0.05)' : undefined}
                        gridSort={gridSort}
                        setGridSort={setGridSort}
                    />
                ) : (
                    <CenterContainer>
                        <FormFieldLabel text="No follow-up tasks found for this shipment" />
                    </CenterContainer>
                )}
            </GridWrapper>

            <Modal
                title={{ text: isEditMode ? 'Edit Follow-up Task' : 'Add Follow-up Task' }}
                width="600px"
                isOpen={isModalOpen}
                onCancelCallback={() => {
                    setIsModalOpen(false);
                    setIsEditMode(false);
                    setSelectedTask(null);
                }}
                Content={() => {
                    return (
                        <ModalContent>
                            <Formik initialValues={setInitialValues()} validate={validate} onSubmit={submit}>
                                {({ values, setFieldValue }) => {
                                    return (
                                        <Form>
                                            <FormGroup>
                                                <FormFieldLabel text="Task" />
                                                <Field name="todo">
                                                    {({ field: { value }, form: { setFieldValue } }: FieldProps) => (
                                                        <TextArea
                                                            name="todo"
                                                            value={value}
                                                            autoSize={{ minRows: 2 }}
                                                            width="300px"
                                                            onChange={(e: any) => {
                                                                setFieldValue('todo', e);
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                            </FormGroup>

                                            <FormGroup>
                                                <FormFieldLabel text="Due Date" />
                                                <Field name="dueDate">
                                                    {({ field, form, meta }: FieldProps) => (
                                                        <CZDatePicker
                                                            name="dueDate"
                                                            style={{ width: '300px' }}
                                                            placeHolder="Select Due Date"
                                                            showTime={true}
                                                            field={field}
                                                            form={form}
                                                        />
                                                    )}
                                                </Field>
                                            </FormGroup>

                                            <FormGroup>
                                                <FormFieldLabel text="Assign To" />
                                                <UserDropDownForm
                                                    name="assignedTo"
                                                    placeHolder={'Select Assignee'}
                                                    users={users}
                                                    customUsers
                                                    showSearch
                                                    currentUserId={currentUserId}
                                                />
                                            </FormGroup>

                                            {isEditMode && (
                                                <FormGroup>
                                                    <FormFieldLabel text="Is Completed" />
                                                    <Field name="isCompleted">
                                                        {({ field }: FieldProps) => (
                                                            <Checkbox
                                                                checked={field.value}
                                                                onChange={(e) => setFieldValue('isCompleted', e.target.checked)}
                                                            />
                                                        )}
                                                    </Field>
                                                </FormGroup>
                                            )}

                                            <ButtonContainer>
                                                <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                                                <Button type="submit">Save</Button>
                                            </ButtonContainer>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </ModalContent>
                    );
                }}
            />
        </PageWrapper>
    );
};

export default FollowUpTab;

const PageWrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 20px;
    gap: 20px;
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const FormGroup = styled.div`
    margin-bottom: 15px;
    width: 100%;
`;

const ModalContent = styled.div`
    padding: 20px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
`;

const GridWrapper = styled.div`
    flex: 1;
    overflow: auto;
    width: 100%;
    z-index: 1;
`;

const CenterContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
`;
